import { memo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Box,
  Typography,
  Card,
  CardContent,
  Skeleton,
  Divider,
  CircularProgress,
  Button,
  useTheme,
} from "@mui/material";
import { Info, Visibility } from "@mui/icons-material";
import RecentItem from "./RecentItem";

const RecentAlertsPanel = memo(
  ({ recentAlerts, isDataLoaded, loading, refreshing, onDeviceClick }) => {
    const theme = useTheme();
    const { t } = useTranslation("common");
    const navigate = useNavigate();

    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(145deg, rgba(40,40,45,0.9) 0%, rgba(35,35,40,0.8) 100%)"
              : "linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(245,245,245,0.8) 100%)",
          backdropFilter: "blur(10px)",
          border: `1px solid ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.1)"
          }`,
          minHeight: 400,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" fontWeight={600}>
              {t("common.recent")}
            </Typography>
            {refreshing && (
              <CircularProgress
                size={16}
                thickness={4}
                sx={{ color: theme.palette.text.secondary }}
              />
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Visibility />}
              onClick={() => navigate("/alerts")}
              sx={{
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              {t("common.view_all")}
            </Button>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        {!isDataLoaded || loading ? (
          <Box>
            {[...Array(5)].map((_, i) => (
              <Card key={i} elevation={0} sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center">
                    <Skeleton
                      variant="circular"
                      width={32}
                      height={32}
                      sx={{ mr: 2 }}
                    />
                    <Box flex={1}>
                      <Skeleton variant="text" width="70%" height={20} />
                      <Skeleton variant="text" width="40%" height={16} />
                    </Box>
                    <Skeleton
                      variant="rectangular"
                      width={60}
                      height={24}
                      sx={{ borderRadius: 1 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : recentAlerts.length > 0 ? (
          <Box>
            {recentAlerts.map((alert, index) => (
              <RecentItem
                key={`alert-${alert.id || index}`}
                timestamp={alert.time_created}
                title={alert.message || "Alert triggered"}
                subtitle={alert.deviceName}
                severity={alert.severity || "info"}
                type="alert"
                deviceId={alert.deviceId}
                onItemClick={onDeviceClick}
              />
            ))}
          </Box>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={8}
          >
            <Info
              sx={{
                fontSize: 64,
                color: theme.palette.text.disabled,
                mb: 2,
              }}
            />
            <Typography
              color="text.secondary"
              textAlign="center"
              variant="h6"
              gutterBottom
            >
              {t("common.no_recent_alerts")}
            </Typography>
            <Typography
              color="text.secondary"
              textAlign="center"
              variant="body2"
            >
              {t("common.recent_alerts_here")}.
            </Typography>
          </Box>
        )}
      </Paper>
    );
  }
);

RecentAlertsPanel.displayName = "RecentAlertsPanel";

export default RecentAlertsPanel;
